/**
 * The editor's row store: the server's rows, the session's unconfirmed
 * patches on top, and the materialised tree of the result (PLAN §6.2
 * "Optimistic UI", §6.3).
 *
 *     confirmed   the newest canonical row per id the server has sent
 *                 (REST load, `row.state`, catch-up, a patch's ack or nack)
 *     pending     this session's patches not yet answered, in send order
 *     view        confirmed ⊕ pending, per row — what the editor shows
 *
 * **Reconciling.** A view row is recomputed from scratch whenever its
 * confirmed row or its pending set changes: the pending patches are
 * re-applied over the new confirmed row with the domain's `applyPatch`, so a
 * remote write that is newer than a pending one wins on its field exactly as
 * it will on the server (per-field LWW), and nothing is double-applied. An
 * ack drops the patch and adopts the canonical row; a nack drops it and
 * adopts the canonical row the server sent back (or keeps the confirmed one),
 * which IS the rollback.
 *
 * **Which confirmed row wins.** Rows arrive by several paths (the ack of a
 * REST patch, the same write's `row.state` fan-out, a catch-up page from 5 s
 * back), in any order; a row's `version` grows by one per write, so the
 * higher version is newer and an equal or lower one is a repeat.
 *
 * The tree (`@acp/domain/tree.js`) is updated incrementally with `upsertRow`;
 * a row that disappears from the view (a nacked creation) is re-inserted as
 * deleted, which hides it — the tree has no removal, and a deleted row is
 * exactly a row the tree does not show.
 *
 * A small external store (`subscribe` / `version`) for React's
 * `useSyncExternalStore`. Pure otherwise: no I/O, no clock.
 */
import { applyPatch } from '@acp/domain/patch.js';
import type { Row, RowPatch } from '@acp/domain/row.js';
import { buildTree, createTree, type RowTree, STANDARD_TREE_OPTIONS, upsertRow } from '@acp/domain/tree.js';

/** `changedAt` of a row that exists only optimistically (the smallest ULID); never sent anywhere. */
const LOCAL_CHANGED_AT = '00000000000000000000000000';

export type RowStore = {
  /** The materialised view (confirmed ⊕ pending). Mutated in place; `version` says when. */
  readonly tree: RowTree;
  /** Bumped on every change a view could render. */
  readonly version: () => number;
  readonly subscribe: (listener: () => void) => () => void;
  /** The row as the editor shows it, or null. */
  readonly get: (rowId: string) => Row | null;
  /** The server's newest row, without this session's pending patches. */
  readonly confirmed: (rowId: string) => Row | null;
  /**
   * Adopt server rows (a load, a catch-up, a `row.state`). Returns the rows
   * that were newer than what we had. `force`: adopt them whatever their
   * version — a listing from ANOTHER host (a failover), whose versions count
   * its own writes, not ours.
   */
  readonly receive: (rows: readonly Row[], force: boolean) => readonly Row[];
  /** Apply this session's patches optimistically. Returns each patch's row as the view had it before (for undo). */
  readonly applyLocal: (patches: readonly RowPatch[]) => readonly (Row | null)[];
  /** The server accepted `clientOpId`; `row` is canonical. */
  readonly ack: (clientOpId: string, row: Row) => void;
  /** The server refused `clientOpId`; roll back to `row` (its canonical state, or null: keep what we had confirmed). */
  readonly nack: (clientOpId: string, row: Row | null) => void;
  /** Whether a patch is still unanswered. */
  readonly isPending: (clientOpId: string) => boolean;
  readonly pendingCount: () => number;
};

/** Fill an EMPTY tree from a full build (the tree's maps are mutable; its identity is what callers hold). */
function seedTree(tree: RowTree, rows: Iterable<Row>): void {
  const built = buildTree(rows, STANDARD_TREE_OPTIONS);
  for (const [key, value] of built.rows) tree.rows.set(key, value);
  for (const [key, value] of built.placement) tree.placement.set(key, value);
  for (const [key, value] of built.children) tree.children.set(key, value);
  for (const [key, value] of built.rawChildren) tree.rawChildren.set(key, value);
}

export function createRowStore(): RowStore {
  const confirmed = new Map<string, Row>();
  const view = new Map<string, Row>();
  const pending = new Map<string, RowPatch>();
  const tree = createTree(STANDARD_TREE_OPTIONS);
  const listeners = new Set<() => void>();
  let version = 0;

  const notify = (): void => {
    version += 1;
    for (const listener of listeners) listener();
  };

  const computeView = (rowId: string): Row | null => {
    let row = confirmed.get(rowId) ?? null;
    for (const patch of pending.values()) {
      if (patch.rowId !== rowId) continue;
      const result = applyPatch(row, patch, { writer: 'native', changedAt: row?.changedAt ?? LOCAL_CHANGED_AT });
      if (result.rejected.length === 0 && result.row !== null) row = result.row;
    }
    return row;
  };

  const refresh = (rowId: string): void => {
    const before = view.get(rowId) ?? null;
    const next = computeView(rowId);
    if (next === before) return;
    if (next === null) {
      view.delete(rowId);
      // Hide it: the tree cannot remove a row, but it never shows a deleted one.
      if (before !== null) upsertRow(tree, { ...before, deleted: true, f: { ...before.f, deleted: true } });
      return;
    }
    view.set(rowId, next);
    upsertRow(tree, next);
  };

  const adopt = (row: Row, force: boolean): boolean => {
    const have = confirmed.get(row.rowId);
    if (!force && have !== undefined && have.version >= row.version) return false;
    confirmed.set(row.rowId, row);
    return true;
  };

  return {
    tree,
    version: () => version,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    get: (rowId) => view.get(rowId) ?? null,
    confirmed: (rowId) => confirmed.get(rowId) ?? null,
    receive: (rows, force) => {
      const newer = rows.filter((row) => adopt(row, force));
      if (newer.length === 0) return [];
      if (tree.rows.size === 0 && pending.size === 0) {
        // First load: one full build instead of N incremental upserts.
        for (const row of newer) view.set(row.rowId, row);
        seedTree(tree, view.values());
      } else {
        for (const row of newer) refresh(row.rowId);
      }
      notify();
      return newer;
    },
    applyLocal: (patches) => {
      const before = patches.map((p) => view.get(p.rowId) ?? null);
      for (const patch of patches) {
        pending.set(patch.clientOpId, patch);
        refresh(patch.rowId);
      }
      notify();
      return before;
    },
    ack: (clientOpId, row) => {
      const patch = pending.get(clientOpId);
      pending.delete(clientOpId);
      adopt(row, false);
      refresh(row.rowId);
      if (patch !== undefined && patch.rowId !== row.rowId) refresh(patch.rowId);
      notify();
    },
    nack: (clientOpId, row) => {
      const patch = pending.get(clientOpId);
      pending.delete(clientOpId);
      if (row !== null) adopt(row, false);
      if (patch !== undefined) refresh(patch.rowId);
      if (row !== null) refresh(row.rowId);
      notify();
    },
    isPending: (clientOpId) => pending.has(clientOpId),
    pendingCount: () => pending.size,
  };
}
